/**
 * Window event fired after this tab changes the multi-product cart (CPS-7479),
 * so cart readers such as the header badge count (useCartCount, CPS-7694) can
 * revalidate without sharing state with the writer.
 */
export const CART_CHANGED_EVENT = 'compass:cart-changed';

/** The same signal for this site's other tabs (CPS-7696). */
export const CART_CHANGED_CHANNEL = 'compass:cart-changed';

let channel: BroadcastChannel | undefined;

/**
 * One channel for sending and hearing, so this tab never hears its own
 * changes back. Missing where BroadcastChannel is, and other tabs then catch
 * up on their own focus refetch.
 */
const otherTabs = (): BroadcastChannel | undefined =>
  (channel ??=
    typeof BroadcastChannel === 'function'
      ? new BroadcastChannel(CART_CHANGED_CHANNEL)
      : undefined);

/** The single call site writers use after a successful cart change. */
export const notifyCartChanged = (): void => {
  window.dispatchEvent(new Event(CART_CHANGED_EVENT));
  otherTabs()?.postMessage(null);
};

/** Readers subscribe here; returns the unsubscribe for an effect cleanup. */
export const subscribeToCartChanged = (listener: () => void): (() => void) => {
  window.addEventListener(CART_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CART_CHANGED_EVENT, listener);
};

/**
 * Another tab's cart change. A hidden tab hears it once it is shown, however
 * many changes it missed, so background tabs do not each spend a read of the
 * shared rate limit on a badge nobody is looking at.
 */
export const subscribeToOtherTabCartChanged = (
  listener: () => void
): (() => void) => {
  let isPending = false;
  const deliver = () => {
    isPending = document.visibilityState === 'hidden';
    if (!isPending) listener();
  };
  const onVisibilityChange = () => {
    if (isPending) deliver();
  };
  otherTabs()?.addEventListener('message', deliver);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    otherTabs()?.removeEventListener('message', deliver);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

/**
 * Window event the PDP fires once its reread after a cart change settles
 * (CPS-7696). That reread runs commerce-core's cart checks, whose tax
 * calculation bumps the cart version, so an open mini-cart rereads too:
 * its next write would otherwise be refused with a 412.
 */
export const CART_REPRICED_EVENT = 'compass:cart-repriced';

export const notifyCartRepriced = (): void => {
  window.dispatchEvent(new Event(CART_REPRICED_EVENT));
};

export const subscribeToCartRepriced = (listener: () => void): (() => void) => {
  window.addEventListener(CART_REPRICED_EVENT, listener);
  return () => window.removeEventListener(CART_REPRICED_EVENT, listener);
};
